/**
 * MailItemDetailPage — `/mail/items/:itemId`.
 *
 * Ported from `screens/src/pages/mail/MailItemDetail.jsx`. The screens
 * prototype renders a rich master/detail view with sender/receiver/payment
 * panels, a state-machine timeline, and a transition action panel. Phase 2b
 * wires the parts that the live API supports today:
 *
 *   - GET /v1/mail-items/:id (operator-scoped) — renders the summary header.
 *   - POST /v1/mail-items/:id/transitions      — drives the transition buttons.
 *
 * Sender/receiver/payment/breakdown panels remain visual (the API doesn't
 * expose those joins on the operator-scoped read today). They surface a
 * DeferredNotice and are purposely empty rather than mocked.
 */
import { type JSX, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Package,
  Truck,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getMailItem,
  transitionMailItem,
  type MailItemDetail,
  type TransitionRequest,
} from '../../lib/api/mail-items';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const STATE_TRANSITIONS: Readonly<Record<string, readonly TransitionRequest['event'][]>> = {
  DRAFT: ['REGISTER'],
  PENDING_PAYMENT: ['PAY', 'REGISTER'],
  REGISTERED: ['SORT', 'DAMAGE'],
  IN_SORTING: ['DISPATCH', 'RETURN', 'DAMAGE'],
  IN_TRANSIT: ['OUT_FOR_DELIVERY', 'RETURN', 'LOSE', 'DAMAGE'],
  OUT_FOR_DELIVERY: ['DELIVER', 'RETURN', 'DAMAGE'],
};

export function MailItemDetailPage(): JSX.Element {
  const params = useParams<{ itemId: string }>();
  const itemId = params.itemId ?? '';
  const queryClient = useQueryClient();
  const [event, setEvent] = useState<TransitionRequest['event'] | ''>('');
  const [notes, setNotes] = useState<string>('');

  const itemQuery = useQuery({
    queryKey: ['mail', 'item', itemId],
    queryFn: () => getMailItem(itemId),
    enabled: itemId.length > 0,
    retry: false,
  });

  const mutate = useMutation({
    mutationFn: (req: TransitionRequest) => transitionMailItem(itemId, req),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail', 'item', itemId] });
      void queryClient.invalidateQueries({ queryKey: ['mail', 'items'] });
      setEvent('');
      setNotes('');
    },
  });

  if (!itemId) {
    return (
      <div>
        <PageHeader title="Mail item" />
        <p className="text-sm text-muted">Missing item id.</p>
      </div>
    );
  }

  if (itemQuery.isPending) {
    return (
      <div>
        <PageHeader title="Mail item" />
        <p className="text-sm text-muted">Loading…</p>
      </div>
    );
  }

  if (itemQuery.isError) {
    const err = itemQuery.error;
    const denied = err instanceof ApiError && (err.status === 401 || err.status === 403);
    const notFound = err instanceof ApiError && err.status === 404;
    return (
      <div>
        <PageHeader
          title="Mail item"
          actions={
            <Link
              to="/mail/items"
              className="inline-flex items-center gap-1 text-sm text-muted hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" /> Back to list
            </Link>
          }
        />
        <DeferredNotice title={denied ? 'Permission required' : notFound ? 'Not found' : 'Service unreachable'}>
          {(err as Error).message}
        </DeferredNotice>
      </div>
    );
  }

  const item = itemQuery.data as MailItemDetail;
  const allowed = STATE_TRANSITIONS[item.status] ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title={item.trackingNumber}
        description={`Service ${item.serviceClass} · operator #${item.operatorId}`}
        actions={
          <Link
            to="/mail/items"
            className="inline-flex items-center gap-1 text-sm text-muted hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" /> Back to list
          </Link>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-border rounded-xl p-4">
          <p className="text-[10px] font-semibold text-muted uppercase tracking-widest">Status</p>
          <div className="mt-2"><StatusBadge status={item.status} /></div>
          <p className="text-[11px] text-muted mt-3">
            Registered {new Date(item.registeredAt).toLocaleString()}
          </p>
          <p className="text-[11px] text-muted">
            Updated {new Date(item.updatedAt).toLocaleString()}
          </p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4 md:col-span-2">
          <p className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-3">
            Identity
          </p>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-muted text-xs">Item id</dt>
            <dd className="font-mono text-xs text-gray-900">{item.id}</dd>
            <dt className="text-muted text-xs">Tracking</dt>
            <dd className="font-mono text-xs text-gray-900">{item.trackingNumber}</dd>
            <dt className="text-muted text-xs">Tenant</dt>
            <dd className="font-mono text-xs text-gray-900">{item.tenantId}</dd>
            <dt className="text-muted text-xs">Registered by</dt>
            <dd className="text-xs text-gray-900">{item.registeredBy}</dd>
          </dl>
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-2">Transition</h3>
        <p className="text-xs text-muted mb-3">
          Status changes are audit-logged with the actor and the changed fields.
        </p>
        {allowed.length === 0 ? (
          <p className="text-xs text-muted">
            No further transitions allowed from <span className="font-mono">{item.status}</span>{' '}
            (terminal or unsupported).
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {allowed.map((ev) => (
                <button
                  key={ev}
                  type="button"
                  onClick={() => setEvent(ev)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg border ${
                    event === ev
                      ? 'bg-nipost-green text-white border-nipost-green'
                      : 'bg-white text-gray-700 border-border hover:bg-gray-50'
                  }`}
                >
                  {iconFor(ev)} {ev}
                </button>
              ))}
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Notes (optional, audit-logged)"
              aria-label="Transition notes"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
            <div className="flex items-center justify-end gap-2">
              {mutate.isError ? (
                <span className="text-xs text-red-700">{(mutate.error as Error).message}</span>
              ) : null}
              <button
                type="button"
                disabled={event === '' || mutate.isPending}
                onClick={() => {
                  if (event !== '') {
                    mutate.mutate({ event, ...(notes.trim() !== '' && { notes: notes.trim() }) });
                  }
                }}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-nipost-green text-white hover:bg-nipost-green-dark disabled:opacity-50"
              >
                {mutate.isPending ? 'Transitioning…' : 'Apply transition'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PartyPanel title="Sender" testId="mid-sender" party={item.sender} />
        <PartyPanel title="Receiver" testId="mid-receiver" party={item.receiver} />
        <PaymentPanel pricing={item.pricing} />
      </div>

      <PackagePanel pkg={item.package} />
    </div>
  );
}

function pickString(record: Readonly<Record<string, unknown>>, ...keys: string[]): string {
  for (const k of keys) {
    const v = record[k];
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return '';
}

function PartyPanel({
  title,
  party,
  testId,
}: {
  title: string;
  party: Readonly<Record<string, unknown>>;
  testId: string;
}): JSX.Element {
  const name = pickString(party, 'name', 'fullName');
  const phone = pickString(party, 'phone', 'phoneNumber');
  const email = pickString(party, 'email');
  const line1 = pickString(party, 'addressLine1', 'line1');
  const line2 = pickString(party, 'addressLine2', 'line2');
  const city = pickString(party, 'city');
  const state = pickString(party, 'state');
  const postal = pickString(party, 'postalCode');
  const country = pickString(party, 'countryCode', 'country');
  const address = [line1, line2, [city, state, postal].filter(Boolean).join(', '), country]
    .filter(Boolean)
    .join(' · ');
  const empty = !name && !phone && !email && !address;
  return (
    <div className="bg-white border border-border rounded-xl p-4" data-testid={testId}>
      <p className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-2">{title}</p>
      {empty ? (
        <p className="text-xs text-muted italic">No {title.toLowerCase()} details on file.</p>
      ) : (
        <dl className="text-sm space-y-1.5">
          {name ? (
            <div>
              <dt className="text-[10px] text-muted uppercase">Name</dt>
              <dd className="text-gray-900 font-medium">{name}</dd>
            </div>
          ) : null}
          {address ? (
            <div>
              <dt className="text-[10px] text-muted uppercase">Address</dt>
              <dd className="text-gray-900 text-xs leading-snug">{address}</dd>
            </div>
          ) : null}
          {phone ? (
            <div>
              <dt className="text-[10px] text-muted uppercase">Phone</dt>
              <dd className="text-gray-900 font-mono text-xs">{phone}</dd>
            </div>
          ) : null}
          {email ? (
            <div>
              <dt className="text-[10px] text-muted uppercase">Email</dt>
              <dd className="text-gray-900 text-xs">{email}</dd>
            </div>
          ) : null}
        </dl>
      )}
    </div>
  );
}

function PaymentPanel({ pricing }: { pricing: Readonly<Record<string, unknown>> }): JSX.Element {
  // The pricing block is a denormalised snapshot of the F-001 quote +
  // optional payment metadata when one of the payment auth flows has
  // touched it. Be tolerant of missing keys.
  const amountNgn = pickString(pricing, 'amountNgn', 'amount', 'totalNgn');
  const amountMinorRaw = pricing['amountMinor'];
  const amountMinor =
    typeof amountMinorRaw === 'number'
      ? amountMinorRaw
      : typeof amountMinorRaw === 'string'
        ? Number.parseInt(amountMinorRaw, 10)
        : null;
  const formattedAmount = formatNaira({ amountNgn, amountMinor });
  const method = pickString(pricing, 'method', 'methodKind', 'paymentMethod');
  const status = pickString(pricing, 'status', 'paymentStatus');
  const reference = pickString(pricing, 'reference', 'paymentReference');
  const empty = !formattedAmount && !method && !status && !reference;
  return (
    <div className="bg-white border border-border rounded-xl p-4" data-testid="mid-payment">
      <p className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-2">Payment</p>
      {empty ? (
        <p className="text-xs text-muted italic">No payment recorded yet.</p>
      ) : (
        <dl className="text-sm space-y-1.5">
          {formattedAmount ? (
            <div>
              <dt className="text-[10px] text-muted uppercase">Amount</dt>
              <dd className="text-gray-900 font-semibold text-base">{formattedAmount}</dd>
            </div>
          ) : null}
          {method ? (
            <div>
              <dt className="text-[10px] text-muted uppercase">Method</dt>
              <dd className="text-gray-900 text-xs">{method}</dd>
            </div>
          ) : null}
          {status ? (
            <div>
              <dt className="text-[10px] text-muted uppercase">Status</dt>
              <dd className="text-gray-900 text-xs font-mono">{status}</dd>
            </div>
          ) : null}
          {reference ? (
            <div>
              <dt className="text-[10px] text-muted uppercase">Reference</dt>
              <dd className="text-gray-900 text-xs font-mono">{reference}</dd>
            </div>
          ) : null}
        </dl>
      )}
    </div>
  );
}

function PackagePanel({ pkg }: { pkg: Readonly<Record<string, unknown>> }): JSX.Element {
  const weight = pickString(pkg, 'weightKg');
  const desc = pickString(pkg, 'contentDescription', 'description');
  const declared = pickString(pkg, 'declaredValueNgn', 'declaredValue');
  const dims = pkg['dimensionsCm'] as
    | { length?: string; width?: string; height?: string }
    | undefined;
  const dimensionStr = dims && (dims.length || dims.width || dims.height)
    ? `${dims.length ?? '?'} × ${dims.width ?? '?'} × ${dims.height ?? '?'} cm`
    : '';
  const empty = !weight && !desc && !declared && !dimensionStr;
  if (empty) return <></>;
  return (
    <div className="bg-white border border-border rounded-xl p-4" data-testid="mid-package">
      <p className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-2">Package</p>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        {weight ? (
          <div>
            <dt className="text-[10px] text-muted uppercase">Weight</dt>
            <dd className="text-gray-900">{weight} kg</dd>
          </div>
        ) : null}
        {dimensionStr ? (
          <div>
            <dt className="text-[10px] text-muted uppercase">Dimensions</dt>
            <dd className="text-gray-900 text-xs">{dimensionStr}</dd>
          </div>
        ) : null}
        {declared ? (
          <div>
            <dt className="text-[10px] text-muted uppercase">Declared value</dt>
            <dd className="text-gray-900">{formatNaira({ amountNgn: declared, amountMinor: null })}</dd>
          </div>
        ) : null}
        {desc ? (
          <div className="col-span-2 sm:col-span-1">
            <dt className="text-[10px] text-muted uppercase">Contents</dt>
            <dd className="text-gray-900 text-xs">{desc}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

function formatNaira({
  amountNgn,
  amountMinor,
}: {
  amountNgn: string;
  amountMinor: number | null;
}): string {
  let value: number | null = null;
  if (amountMinor !== null && Number.isFinite(amountMinor)) {
    value = amountMinor / 100;
  } else if (amountNgn) {
    const n = Number.parseFloat(amountNgn);
    if (Number.isFinite(n)) value = n;
  }
  if (value === null) return '';
  return `₦${value.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function iconFor(ev: TransitionRequest['event']): JSX.Element {
  if (ev === 'DELIVER') return <CheckCircle2 className="inline w-3 h-3 mr-1" />;
  if (ev === 'OUT_FOR_DELIVERY' || ev === 'DISPATCH') return <Truck className="inline w-3 h-3 mr-1" />;
  if (ev === 'SORT') return <Package className="inline w-3 h-3 mr-1" />;
  if (ev === 'DAMAGE' || ev === 'LOSE') return <AlertTriangle className="inline w-3 h-3 mr-1" />;
  return <Clock className="inline w-3 h-3 mr-1" />;
}
